import { useState, useEffect, useRef } from "react";
import type { Suggestion } from "../types";
import { API_ENDPOINTS } from "../constants/api";

interface Props {
  value: string;
  onChange: (value: string) => void;
  onSelect?: (name: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export default function AutocompleteInput({ value, onChange, onSelect, placeholder, disabled, className }: Props) {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(-1);
  const debounceRef = useRef<number | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const fetchSuggestions = (q: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (q.length < 1) { setSuggestions([]); setShowSuggestions(false); return; }
    debounceRef.current = window.setTimeout(async () => {
      try {
        const res = await fetch(API_ENDPOINTS.autocomplete(q));
        const data = await res.json();
        setSuggestions(data);
        setShowSuggestions(data.length > 0);
        setSelectedIdx(-1);
      } catch { setSuggestions([]); }
    }, 200);
  };

  const handleChange = (val: string) => {
    onChange(val);
    fetchSuggestions(val);
  };

  const handleSelect = (name: string) => {
    onChange(name);
    setShowSuggestions(false);
    setSuggestions([]);
    onSelect?.(name);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIdx((prev) => Math.min(prev + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIdx((prev) => Math.max(prev - 1, 0));
    } else if (e.key === "Enter" && selectedIdx >= 0) {
      e.preventDefault();
      handleSelect(suggestions[selectedIdx].name);
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
    }
  };

  return (
    <div ref={wrapperRef} className="relative flex-1 max-md:w-full">
      <input
        type="text"
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        className={className}
      />
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute left-0 right-0 top-full mt-1 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl shadow-lg z-50 overflow-hidden max-h-60 overflow-y-auto">
          {suggestions.map((s, i) => (
            <button
              key={s.code}
              type="button"
              onClick={() => handleSelect(s.name)}
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-left cursor-pointer transition-colors ${i === selectedIdx ? "bg-indigo-50 dark:bg-indigo-950" : "hover:bg-zinc-50 dark:hover:bg-zinc-800"}`}
            >
              <div className="flex-1 min-w-0">
                <span className="text-sm font-semibold text-zinc-950 dark:text-zinc-100">{s.name}</span>
                <span className="text-xs text-zinc-400 ml-2">{s.code} · {s.market}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
